import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, AlertTriangle, Loader2, Plus, X } from "lucide-react";
import { useState, useEffect } from "react";
import { SkillSelector } from "@/components/SkillSelector";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import { useJobPosts } from "@/hooks/useJobPosts";
import { toast } from "@/hooks/use-toast";
import { EMPLOYMENT_TYPES, WORK_MODELS } from "@/lib/constants";

const statusOptions = [
  { label: "Rascunho", value: "rascunho" },
  { label: "Em Revisão", value: "em_revisao" },
  { label: "Publicada", value: "publicada" },
  { label: "Pausada", value: "pausada" },
  { label: "Encerrada", value: "encerrada" },
];

export default function AdminEditarVaga() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { orgs, orgId, setSelectedOrgId, isLoading: orgsLoading } = useAdminOrgContext();
  const { posts, isLoading: postsLoading, updatePost } = useJobPosts(orgId);
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [niceSkills, setNiceSkills] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [newBenefit, setNewBenefit] = useState("");
  const [processSteps, setProcessSteps] = useState<string[]>([]);
  const [newStep, setNewStep] = useState("");

  const [formData, setFormData] = useState({
    titulo: "", tipo: "", modelo: "", cidade: "", uf: "", descricao: "",
    responsabilidades: "", requisitosObrigatorios: "", requisitosDesejaveis: "",
    prazo: "", status: "rascunho", stipendMin: "", stipendMax: "",
  });

  const post = posts.find((p) => p.id === id);

  useEffect(() => {
    if (post && !loaded) {
      const p = post as any;
      setFormData({
        titulo: post.title || "",
        tipo: post.employment_type || "",
        modelo: post.work_model || "",
        cidade: post.location_city || "",
        uf: post.location_state || "",
        descricao: post.description || "",
        responsabilidades: Array.isArray(post.responsibilities) ? (post.responsibilities as string[]).join("\n") : "",
        requisitosObrigatorios: Array.isArray(post.requirements_must) ? (post.requirements_must as string[]).join("\n") : "",
        requisitosDesejaveis: Array.isArray(p.requirements_nice) ? (p.requirements_nice as string[]).join("\n") : "",
        prazo: post.deadline_date || "",
        status: post.status || "rascunho",
        stipendMin: p.stipend_min ? String(p.stipend_min) : "",
        stipendMax: p.stipend_max ? String(p.stipend_max) : "",
      });
      setRequiredSkills(Array.isArray(post.required_skills) ? (post.required_skills as string[]) : []);
      setNiceSkills(Array.isArray(post.nice_skills) ? (post.nice_skills as string[]) : []);
      setBenefits(Array.isArray(p.benefits) ? p.benefits : []);
      setProcessSteps(Array.isArray(p.process_steps) ? p.process_steps : []);
      setLoaded(true);
    }
  }, [post, loaded]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "requisitosObrigatorios") {
      const terms = ["anos de experiência", "experiência prévia", "experiência comprovada"];
      setShowWarning(terms.some((t) => value.toLowerCase().includes(t)));
    }
  };

  const addBenefit = () => { if (newBenefit.trim()) { setBenefits(prev => [...prev, newBenefit.trim()]); setNewBenefit(""); } };
  const addStep = () => { if (newStep.trim()) { setProcessSteps(prev => [...prev, newStep.trim()]); setNewStep(""); } };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !orgId) return;
    if (!formData.titulo.trim()) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const wasPublished = post?.status !== "publicada" && formData.status === "publicada";
      await updatePost.mutateAsync({
        id,
        title: formData.titulo,
        employment_type: formData.tipo || "aprendiz",
        work_model: formData.modelo || null,
        location_city: formData.cidade || null,
        location_state: formData.uf || null,
        description: formData.descricao || null,
        responsibilities: formData.responsabilidades ? formData.responsabilidades.split("\n").filter(Boolean) : null,
        requirements_must: formData.requisitosObrigatorios ? formData.requisitosObrigatorios.split("\n").filter(Boolean) : null,
        requirements_nice: formData.requisitosDesejaveis ? formData.requisitosDesejaveis.split("\n").filter(Boolean) : [],
        required_skills: requiredSkills.length > 0 ? requiredSkills : null,
        nice_skills: niceSkills.length > 0 ? niceSkills : null,
        deadline_date: formData.prazo || null,
        stipend_min: formData.stipendMin ? (parseInt(formData.stipendMin, 10) || null) : null,
        stipend_max: formData.stipendMax ? (parseInt(formData.stipendMax, 10) || null) : null,
        benefits: benefits.length > 0 ? benefits : [],
        process_steps: processSteps.length > 0 ? processSteps : [],
        status: formData.status,
        published_at: wasPublished ? new Date().toISOString() : undefined,
      } as any);
      toast({ title: "Vaga atualizada!" });
      navigate("/admin/publicar-vagas");
    } catch (err: any) {
      toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (orgsLoading || postsLoading) {
    return <div className="p-8 flex justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!post) {
    return <div className="p-8 text-center"><p className="text-muted-foreground">Vaga não encontrada.</p></div>;
  }

  return (
    <div className="p-8 max-w-3xl">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/publicar-vagas")}><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Editar Vaga</h1>
          <p className="text-muted-foreground text-sm">Atualize os dados da vaga</p>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground">Organização</h2>
          <Select value={orgId ?? ""} onValueChange={setSelectedOrgId}>
            <SelectTrigger><SelectValue placeholder="Selecione a organização" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => (<SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>))}</SelectContent>
          </Select>
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Status da Vaga</h2>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((s) => (
              <Badge key={s.value} variant={formData.status === s.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("status", s.value)}>
                {formData.status === s.value && <Check className="w-3 h-3" />} {s.label}
              </Badge>
            ))}
          </div>
        </div>

        <div className="bg-success/10 rounded-xl p-4 border border-success/20">
          <p className="text-sm text-foreground font-medium">🎯 Vagas de entrada — sem exigir experiência prévia</p>
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Informações Básicas</h2>
          <div className="space-y-2">
            <Label htmlFor="titulo">Título da vaga *</Label>
            <Input id="titulo" value={formData.titulo} onChange={(e) => handleChange("titulo", e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <div className="flex flex-wrap gap-2">
                {EMPLOYMENT_TYPES.map((t) => (
                  <Badge key={t.value} variant={formData.tipo === t.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("tipo", t.value)}>
                    {formData.tipo === t.value && <Check className="w-3 h-3" />} {t.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Modelo *</Label>
              <div className="flex flex-wrap gap-2">
                {WORK_MODELS.map((m) => (
                  <Badge key={m.value} variant={formData.modelo === m.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("modelo", m.value)}>
                    {formData.modelo === m.value && <Check className="w-3 h-3" />} {m.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          {formData.modelo !== "remoto" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cidade</Label><Input value={formData.cidade} onChange={(e) => handleChange("cidade", e.target.value)} /></div>
              <div className="space-y-2"><Label>UF</Label><Input maxLength={2} value={formData.uf} onChange={(e) => handleChange("uf", e.target.value.toUpperCase())} /></div>
            </div>
          )}
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Descrição</h2>
          <Textarea placeholder="Descreva a vaga..." value={formData.descricao} onChange={(e) => handleChange("descricao", e.target.value)} rows={4} />
          <Textarea placeholder="Responsabilidades (uma por linha)..." value={formData.responsabilidades} onChange={(e) => handleChange("responsabilidades", e.target.value)} rows={3} />
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Requisitos</h2>
          <Textarea placeholder="Requisitos obrigatórios (um por linha)..." value={formData.requisitosObrigatorios} onChange={(e) => handleChange("requisitosObrigatorios", e.target.value)} rows={3} />
          {showWarning && (
            <div className="bg-warning/10 rounded-xl p-4 border border-warning/20 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning shrink-0" />
              <p className="text-sm font-medium text-foreground">Evite exigir experiência prévia</p>
            </div>
          )}
          <Label>Requisitos desejáveis</Label>
          <Textarea placeholder="Requisitos desejáveis (um por linha)..." value={formData.requisitosDesejaveis} onChange={(e) => handleChange("requisitosDesejaveis", e.target.value)} rows={2} />
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-6">
          <h2 className="font-heading font-semibold text-foreground mb-2">Habilidades</h2>
          <SkillSelector selectedSkills={requiredSkills} onChange={setRequiredSkills} maxSkills={6} label="Obrigatórias (até 6)" />
          <SkillSelector selectedSkills={niceSkills} onChange={(s) => setNiceSkills(s.filter((x) => !requiredSkills.includes(x)))} maxSkills={8} label="Desejáveis (até 8)" />
        </div>

        {/* Stipend & Benefits */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Remuneração e Benefícios</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Bolsa-auxílio mínima (R$)</Label><Input type="number" placeholder="800" value={formData.stipendMin} onChange={(e) => handleChange("stipendMin", e.target.value)} /></div>
            <div className="space-y-2"><Label>Bolsa-auxílio máxima (R$)</Label><Input type="number" placeholder="1500" value={formData.stipendMax} onChange={(e) => handleChange("stipendMax", e.target.value)} /></div>
          </div>
          <div className="space-y-2">
            <Label>Benefícios</Label>
            <div className="flex gap-2">
              <Input placeholder="Ex: Vale-transporte" value={newBenefit} onChange={(e) => setNewBenefit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBenefit(); } }} />
              <Button type="button" variant="outline" size="icon" onClick={addBenefit}><Plus className="w-4 h-4" /></Button>
            </div>
            {benefits.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {benefits.map((b, i) => (<Badge key={i} variant="secondary" className="gap-1">{b} <X className="w-3 h-3 cursor-pointer" onClick={() => setBenefits(prev => prev.filter((_, j) => j !== i))} /></Badge>))}
              </div>
            )}
          </div>
        </div>

        {/* Process Steps */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Etapas do Processo Seletivo</h2>
          <div className="flex gap-2">
            <Input placeholder="Ex: Inscrição online" value={newStep} onChange={(e) => setNewStep(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStep(); } }} />
            <Button type="button" variant="outline" size="icon" onClick={addStep}><Plus className="w-4 h-4" /></Button>
          </div>
          {processSteps.length > 0 && (
            <ol className="space-y-2 mt-2">
              {processSteps.map((s, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 text-foreground">{s}</span>
                  <X className="w-4 h-4 text-muted-foreground cursor-pointer" onClick={() => setProcessSteps(prev => prev.filter((_, j) => j !== i))} />
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <Label>Prazo para candidatura</Label>
          <Input type="date" value={formData.prazo} onChange={(e) => handleChange("prazo", e.target.value)} />
        </div>

        <div className="flex gap-4">
          <Button type="button" variant="outline" size="lg" className="flex-1" onClick={() => navigate("/admin/publicar-vagas")}>Cancelar</Button>
          <Button type="submit" variant="hero" size="lg" className="flex-1" disabled={isLoading || !orgId}>
            {isLoading ? (<><Loader2 className="w-5 h-5 animate-spin" />Salvando...</>) : "Salvar Alterações"}
          </Button>
        </div>
      </form>
    </div>
  );
}
